import type { HTMLAttributes, RefAttributes, ReactNode } from "react";
import { memo, useMemo } from "react";
import { cn } from "@/shared/libs/commonUtils";
import { isString } from "@/shared/libs/type-utils";

type Responsive<T> = T | { default?: T; sm?: T; md?: T; lg?: T; xl?: T };

type Direction = "row" | "col" | "row-reverse" | "col-reverse";
type Wrap = "nowrap" | "wrap" | "wrap-reverse";
type JustifyContent =
  | "start"
  | "end"
  | "center"
  | "between"
  | "around"
  | "evenly";
type AlignContent =
  | "start"
  | "end"
  | "center"
  | "between"
  | "around"
  | "evenly"
  | "stretch";
type AlignItems = "start" | "end" | "center" | "stretch" | "baseline";
type JustifyItems = "start" | "end" | "center" | "stretch" | "baseline";

// Flex item props
type JustifySelf = "start" | "end" | "center" | "stretch" | "baseline";
type AlignSelf = "start" | "end" | "center" | "stretch" | "baseline";
type Basis =
  | "0"
  | "0.5"
  | "1"
  | "1.5"
  | "2"
  | "2.5"
  | "3"
  | "3.5"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12"
  | "14"
  | "16"
  | "20"
  | "24"
  | "28"
  | "32"
  | "36"
  | "40"
  | "44"
  | "48"
  | "52"
  | "56"
  | "60"
  | "64"
  | "72"
  | "80"
  | "96"
  | "auto"
  | "full"
  | "1/2"
  | "1/3"
  | "2/3"
  | "1/4"
  | "2/4"
  | "3/4"
  | "1/5"
  | "2/5"
  | "3/5"
  | "4/5"
  | "1/6"
  | "2/6"
  | "3/6"
  | "4/6"
  | "5/6"
  | "1/12"
  | "2/12"
  | "3/12"
  | "4/12"
  | "5/12"
  | "6/12"
  | "7/12"
  | "8/12"
  | "9/12"
  | "10/12"
  | "11/12"
  | "px"
  | "fit"
  | "max"
  | "min";

interface FlexProps
  extends HTMLAttributes<HTMLDivElement>,
    RefAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
  direction?: Responsive<Direction>;
  wrap?: Responsive<Wrap>;
  justifyContent?: Responsive<JustifyContent>;
  alignContent?: Responsive<AlignContent>;
  alignItems?: Responsive<AlignItems>;
  justifyItems?: Responsive<JustifyItems>;
  gap?: Responsive<string>;
}

interface FlexItemProps
  extends HTMLAttributes<HTMLDivElement>,
    RefAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
  justifySelf?: Responsive<JustifySelf>;
  alignSelf?: Responsive<AlignSelf>;
  basis?: Responsive<Basis>;
}

const getResponsiveClass = <T extends string>(
  prop: Responsive<T> | undefined,
  mapper: (value: T) => string,
): string => {
  if (!prop) return "";
  if (isString(prop)) return mapper(prop as T);

  return Object.entries(prop)
    .map(([breakpoint, value]) =>
      breakpoint === "default"
        ? mapper(value as T)
        : `${breakpoint}:${mapper(value as T)}`,
    )
    .join(" ");
};

const Flex = function Flex({
  children,
  className,
  direction = "row",
  wrap = "nowrap",
  justifyContent = "start",
  alignContent = "stretch",
  alignItems = "stretch",
  justifyItems = "start",
  gap,
  ...props
}: FlexProps) {
  const flexClassMap = {
    direction: (value: Direction) => `flex-${value}`,
    wrap: (value: Wrap) => `flex-${value}`,
    justifyContent: (value: JustifyContent) => `justify-${value}`,
    alignContent: (value: AlignContent) => `content-${value}`,
    alignItems: (value: AlignItems) => `items-${value}`,
    justifyItems: (value: JustifyItems) => `justify-items-${value}`,
    gap: (value: string) => `gap-${value}`,
  };

  const computedClassName = useMemo(
    () =>
      cn(
        "flex",
        getResponsiveClass(direction, flexClassMap.direction),
        getResponsiveClass(wrap, flexClassMap.wrap),
        getResponsiveClass(justifyContent, flexClassMap.justifyContent),
        getResponsiveClass(alignContent, flexClassMap.alignContent),
        getResponsiveClass(alignItems, flexClassMap.alignItems),
        getResponsiveClass(justifyItems, flexClassMap.justifyItems),
        getResponsiveClass(gap, flexClassMap.gap),
        className,
      ),
    [
      direction,
      wrap,
      justifyContent,
      alignContent,
      alignItems,
      justifyItems,
      gap,
      className,
      flexClassMap.alignContent,
      flexClassMap.alignItems,
      flexClassMap.direction,
      flexClassMap.gap,
      flexClassMap.justifyContent,
      flexClassMap.justifyItems,
      flexClassMap.wrap,
    ],
  );

  return (
    <div {...props} className={computedClassName}>
      {children}
    </div>
  );
};

/**
 * A flexible container component using Tailwind CSS flexbox utilities.
 * @param {FlexProps} props - Props for configuring the flex container.
 * @example
 * <Flex direction="row" justifyContent="between" alignItems="center" gap="4">
 *   <Flex.Item>Item 1</Flex.Item>
 *   <Flex.Item>Item 2</Flex.Item>
 * </Flex>
 */

const FlexItem = memo(function FlexItem({
  children,
  className,
  justifySelf,
  alignSelf,
  basis,
  ...props
}: FlexItemProps) {
  const itemClassMap = {
    justifySelf: (value: JustifySelf) => `justify-self-${value}`,
    alignSelf: (value: AlignSelf) => `self-${value}`,
    basis: (value: Basis) => `basis-${value}`,
  };

  const computedClassName = useMemo(
    () =>
      cn(
        getResponsiveClass(justifySelf, itemClassMap.justifySelf),
        getResponsiveClass(alignSelf, itemClassMap.alignSelf),
        getResponsiveClass(basis, itemClassMap.basis), // Added basis class
        className,
      ),
    [
      justifySelf,
      alignSelf,
      className,
      basis,
      itemClassMap.justifySelf,
      itemClassMap.alignSelf,
      itemClassMap.basis,
    ],
  );

  return (
    <div {...props} className={computedClassName}>
      {children}
    </div>
  );
});

/**
 * A flex item component for use within a Flex container, applying item-level Tailwind styles.
 * @param {FlexItemProps} props - Props for configuring the flex item.
 * @example
 * <Flex direction="row">
 *   <Flex.Item justifySelf="center" alignSelf="stretch">Item</Flex.Item>
 * </Flex>
 */

Flex.Item = FlexItem;

export default Flex;
